<template>
  <div>
    <component :is="currentComponent" v-bind="componentProps" />
  </div>
</template>

<script setup lang="ts">
import { defineAsyncComponent, computed, watch } from 'vue'
import { useRoute } from 'vue-router'

import { marked } from 'marked'
import { usePageStore } from '@/store/page'
import { useTenant } from '@/composables/useTenant'
import { sanitizeSlug } from '@/types/content'

import { useSupportStore } from '@/store/support'
import { type SupportArticle, type SupportCategory } from '@/api/useSupport'

marked.use({ gfm: true, breaks: true })

const CategoriesPage = defineAsyncComponent(() => import('@/pages/categories/[detail]/index.vue'))
const ContentPage = defineAsyncComponent(() => import('@/pages/content/[detail]/index.vue'))

// One page instance per path. Nuxt already keys `<NuxtPage>` this way by
// default; stating it here makes the snapshot-based resolution below depend on
// a declared contract rather than on that default.
definePageMeta({ key: (route) => route.fullPath })

const route = useRoute()
const { page } = usePageStore()

const detail = computed(() => (route.params.detail as string) || '')

const supportStore = useSupportStore()

// Nuxt context dependencies (must be resolved at top-level setup, not after awaits).
const runtimeConfig = useRuntimeConfig()
const apiBaseUrl = runtimeConfig.public.apiBaseUrl as string
const nuxtApp = useNuxtApp()
const i18nLocale = (nuxtApp.$i18n as any)?.locale
const setAppLocale = nuxtApp.$setAppLocale as ((locale: string) => Promise<void>) | undefined
const language = computed(() => String(i18nLocale?.value || 'tr').split('-')[0] || 'tr')
const tenantRef = useTenant()

type SupportCategoryItem = {
  id: string
  title: string
  slug: string
  type: 'content' | 'category'
  items?: SupportCategoryItem[]
}

type SupportCategoryPagePayload = {
  id: string
  title: string
  slug: string
  items: SupportCategoryItem[]
  categories: SupportCategoryItem[]
}

function renderMarkdown(md: string): string {
  if (!md) return ''
  // marked.parse default sync; markdown stringi HTML'e çevirir.
  return marked.parse(md) as string
}

function mapSupportArticleToContent(a: SupportArticle | null) {
  if (!a) return null
  return {
    id: String(a.id),
    title: a.title,
    published_at: a.date_updated || '',
    read_duration_minutes: 0,
    content: {
      web: renderMarkdown(a.body.web ?? ''),
      mobile: renderMarkdown(a.body.app ?? ''),
    },
    seo: a.seo,
    categories: [
      {
        id: String(a.category.id),
        title: a.category.name,
        slug: a.category.slug,
      },
    ],
  }
}

function mapSupportCategoryToCategoriesPage(
  c: SupportCategory,
  parents: SupportCategory[] = [],
): SupportCategoryPagePayload {
  const items: SupportCategoryItem[] = [
    ...c.articles.map((a) => ({
      id: String(a.id),
      title: a.title,
      slug: a.slug,
      type: 'content' as const,
    })),
    ...c.subcategories.map((sc) => ({
      id: String(sc.id),
      title: sc.name,
      slug: sc.slug,
      type: 'category' as const,
      items: sc.articles.map((a) => ({
        id: String(a.id),
        title: a.title,
        slug: a.slug,
        type: 'content' as const,
      })),
    })),
  ]

  return {
    id: String(c.id),
    title: c.name,
    slug: c.slug,
    items,
    categories: parents.map((p) => ({
      id: String(p.id),
      title: p.name,
      slug: p.slug,
      type: 'category' as const,
    })),
  }
}

type ResolutionType = 'SupportContent' | 'SupportCategories' | 'Categories' | 'Content'

interface Resolution {
  type: ResolutionType | null
  supportArticle: SupportArticle | null
  supportCategoryData: SupportCategoryPagePayload | null
  categories: any | null
  content: any | null
}

async function fetchLegacyCategories(
  slug: string,
  signal?: AbortSignal,
  lang: string = language.value,
): Promise<any | null> {
  const slugToUse = sanitizeSlug(slug)
  if (!slugToUse) return null
  try {
    const response = await $fetch<{ payload: any }>(`/category/tree/${slugToUse}`, {
      baseURL: apiBaseUrl,
      headers: { 'Accept-Language': lang },
      query: { tenant: tenantRef.value, lang },
      signal,
    })
    return response?.payload ?? null
  } catch {
    return null
  }
}

async function fetchLegacyContent(
  slug: string,
  signal?: AbortSignal,
  lang: string = language.value,
): Promise<any | null> {
  const slugToUse = sanitizeSlug(slug)
  if (!slugToUse) return null
  try {
    const response = await $fetch<{ payload: any }>(`/content/${slugToUse}`, {
      baseURL: apiBaseUrl,
      headers: { 'Accept-Language': lang },
      query: { tenant: tenantRef.value },
      signal,
    })
    return response?.payload ?? null
  } catch {
    return null
  }
}

function findArticleIdInTree(cats: SupportCategory[], slug: string): number | null {
  for (const cat of cats) {
    const direct = cat.articles ?? []
    const fromSub = (cat.subcategories ?? []).flatMap((sc) => sc.articles ?? [])
    const hit = [...direct, ...fromSub].find((a) => a.slug === slug)
    if (hit) return hit.id
  }
  return null
}

function findCategoryInTree(
  cats: SupportCategory[],
  slug: string,
): { node: SupportCategory; parents: SupportCategory[] } | null {
  for (const cat of cats) {
    if (cat.slug === slug) return { node: cat, parents: [] }
    for (const sc of cat.subcategories ?? []) {
      if (sc.slug === slug) return { node: sc, parents: [cat] }
    }
  }
  return null
}

async function fetchSupportTree(
  lang: string,
  signal?: AbortSignal,
): Promise<SupportCategory[] | null> {
  try {
    const response = await $fetch<{ success: boolean; data: { categories: SupportCategory[] } }>(
      '/api/v1/customer/support/categories',
      { baseURL: apiBaseUrl, query: { lang }, signal },
    )
    return response?.data?.categories ?? null
  } catch {
    return null
  }
}

async function fetchSupportArticleById(
  id: number,
  lang: string,
  signal?: AbortSignal,
): Promise<SupportArticle | null> {
  try {
    const response = await $fetch<{ success: boolean; data: SupportArticle }>(
      `/api/v1/customer/support/articles/${id}`,
      { baseURL: apiBaseUrl, headers: { 'Accept-Language': lang }, query: { lang }, signal },
    )
    return response?.data ?? null
  } catch {
    return null
  }
}

// Slug diğer dile aitse uygulamanın dilini o dile çevirir; cookie + i18n locale
// + çeviriler + support store birlikte güncellenir ki SSR yanıtı ve sonraki
// client navigasyonları tutarlı kalsın.
async function switchLocaleTo(lang: string, cats: SupportCategory[] | null): Promise<void> {
  if (setAppLocale) {
    try {
      await setAppLocale(lang)
    } catch {
      // çeviri yüklenemese de dil geçişi devam eder
    }
  } else if (i18nLocale) {
    i18nLocale.value = lang
  }
  if (cats) {
    supportStore.$patch({
      categories: cats,
      fetched: true,
      cacheKey: lang,
      loading: false,
      error: null,
    })
  } else {
    supportStore.resetCache()
  }
}

// Mevcut dilde bulunamayan slug'ı diğer dilde arar. Bulursa locale'i o dile
// çevirip resolution'ı doldurur (TR cookie ile açılan EN linki 404 yerine
// sayfayı İngilizce açar — ve tersi).
async function resolveFromOtherLanguage(
  newSlug: string,
  result: Resolution,
  signal?: AbortSignal,
): Promise<boolean> {
  if (signal?.aborted) return false
  const otherLang = language.value === 'tr' ? 'en' : 'tr'

  // Support tree + legacy uçları paralel; öncelik yine support tree'de.
  const [cats, legacyCat, legacyContent] = await Promise.all([
    fetchSupportTree(otherLang, signal),
    fetchLegacyCategories(newSlug, signal, otherLang),
    fetchLegacyContent(newSlug, signal, otherLang),
  ])

  if (cats) {
    const articleId = findArticleIdInTree(cats, newSlug)
    if (articleId) {
      const article = await fetchSupportArticleById(articleId, otherLang, signal)
      if (article) {
        await switchLocaleTo(otherLang, cats)
        result.supportArticle = article
        result.type = 'SupportContent'
        return true
      }
    }

    const catHit = findCategoryInTree(cats, newSlug)
    if (catHit) {
      await switchLocaleTo(otherLang, cats)
      result.supportCategoryData = mapSupportCategoryToCategoriesPage(catHit.node, catHit.parents)
      result.type = 'SupportCategories'
      return true
    }
  }

  // Legacy içerik/kategori de diğer dilde olabilir
  if (legacyCat) {
    await switchLocaleTo(otherLang, cats)
    result.categories = legacyCat
    result.type = 'Categories'
    return true
  }
  if (legacyContent) {
    await switchLocaleTo(otherLang, cats)
    result.content = legacyContent
    result.type = 'Content'
    return true
  }

  return false
}

async function resolveSlug(
  newSlug: string,
  hintType: 'category' | 'content' | null,
  signal?: AbortSignal,
): Promise<Resolution> {
  const result: Resolution = {
    type: null,
    supportArticle: null,
    supportCategoryData: null,
    categories: null,
    content: null,
  }

  if (!newSlug) return result

  // 1. Support API'den makale eşleşmesini dene
  if (!supportStore.fetched) {
    await supportStore.fetchSupportCategories()
  }
  const cats = supportStore.categories || []

  const articleId = findArticleIdInTree(cats, newSlug)
  if (articleId) {
    const article = await fetchSupportArticleById(articleId, language.value, signal)
    if (article) {
      result.supportArticle = article
      result.type = 'SupportContent'
      return result
    }
  }

  // 2. Support kategorisi eşleşmesini dene
  const catHit = findCategoryInTree(cats, newSlug)
  if (catHit) {
    result.supportCategoryData = mapSupportCategoryToCategoriesPage(catHit.node, catHit.parents)
    result.type = 'SupportCategories'
    return result
  }

  // 3. Hint'e göre legacy fetch (doğrudan $fetch — Nuxt context after-await sorunu yok)
  if (hintType === 'category') {
    const catData = await fetchLegacyCategories(newSlug, signal)
    if (catData) {
      result.categories = catData
      result.type = 'Categories'
      return result
    }
    if (await resolveFromOtherLanguage(newSlug, result, signal)) return result
    if (signal?.aborted) return result
    showError({ statusCode: 404 })
    return result
  }

  if (hintType === 'content') {
    const contentData = await fetchLegacyContent(newSlug, signal)
    if (contentData) {
      result.content = contentData
      result.type = 'Content'
      return result
    }
    // Content yoksa categories fallback
    const catData = await fetchLegacyCategories(newSlug, signal)
    if (catData) {
      result.categories = catData
      result.type = 'Categories'
      return result
    }
    if (await resolveFromOtherLanguage(newSlug, result, signal)) return result
    if (signal?.aborted) return result
    showError({ statusCode: 404 })
    return result
  }

  // Hint yok: paralel dene
  const [catData, contentData] = await Promise.all([
    fetchLegacyCategories(newSlug, signal),
    fetchLegacyContent(newSlug, signal),
  ])
  if (catData) {
    result.categories = catData
    result.type = 'Categories'
  } else if (contentData) {
    result.content = contentData
    result.type = 'Content'
  } else {
    if (await resolveFromOtherLanguage(newSlug, result, signal)) return result
    if (signal?.aborted) return result
    showError({ statusCode: 404 })
  }
  return result
}

// Resolve against snapshots taken at setup rather than the live route.
// `useRoute()` is global and reactive: on a client navigation inside this same
// route record it flips to the incoming slug *before* the outgoing page
// instance unmounts. With one shared asyncData key plus `watch: [detail]`, that
// outgoing instance re-resolved the incoming slug — so every such navigation
// ran the resolver twice and article pages fetched `/articles/{id}` twice.
// A per-slug key plus the page key above give each navigation a fresh instance
// that resolves its own slug exactly once; the outgoing one has nothing
// reactive left to react to.
const detailSnapshot = detail.value
// The hint is single-use and cleared by the watcher below, so read it here
// instead of inside the handler — that keeps it independent of when the
// clearing watcher happens to flush relative to this setup.
const hintSnapshot = page.type

const { data: resolution } = await useAsyncData(
  `slug-resolution:${detailSnapshot}`,
  (ctx) => resolveSlug(detailSnapshot, hintSnapshot, ctx?.signal as AbortSignal | undefined),
  // No `watch` on purpose: the key already changes on every navigation, and a
  // watcher here is exactly what let the outgoing instance re-resolve.
  { watch: [] },
)

watch(
  resolution,
  (r) => {
    if (!r) {
      page.currentArticleId = null
      page.currentCategoryId = null
      return
    }
    if (r.type === 'SupportContent') {
      page.currentArticleId = r.supportArticle ? r.supportArticle.id : null
      page.currentCategoryId = null
      return
    }
    if (r.type === 'SupportCategories') {
      const cid = r.supportCategoryData?.id
      page.currentCategoryId = cid != null ? Number(cid) : null
      page.currentArticleId = null
      return
    }
    if (r.type === 'Content') {
      const aid = r.content?.id
      page.currentArticleId = aid != null ? Number(aid) : null
      page.currentCategoryId = null
      return
    }
    if (r.type === 'Categories') {
      const cid = r.categories?.id
      page.currentCategoryId = cid != null ? Number(cid) : null
      page.currentArticleId = null
      return
    }
    page.currentArticleId = null
    page.currentCategoryId = null
  },
  { immediate: true },
)

// page.type hint'i tüketildikten sonra temizle: stale hint sonraki nav'a sızmasın.
watch(
  detail,
  () => {
    if (import.meta.client) {
      page.type = null
      page.currentArticleId = null
      page.currentCategoryId = null
    }
  },
  { flush: 'post' },
)

const isContentPlaceholder = computed(() => Boolean(route.params.category))

const currentComponent = computed(() => {
  const t = resolution.value?.type
  if (t === 'SupportContent' || t === 'Content') return ContentPage
  if (t === 'SupportCategories' || t === 'Categories') return CategoriesPage
  return isContentPlaceholder.value ? ContentPage : CategoriesPage
})

const componentProps = computed(() => {
  const r = resolution.value
  if (r?.type === 'SupportContent') {
    return { content: mapSupportArticleToContent(r.supportArticle), loading: false }
  }
  if (r?.type === 'SupportCategories') {
    return { categories: r.supportCategoryData, loading: false }
  }
  if (r?.type === 'Categories') {
    return { categories: r.categories, loading: false }
  }
  if (r?.type === 'Content') {
    return { content: r.content, loading: false }
  }
  return isContentPlaceholder.value
    ? { content: null, loading: true }
    : { categories: null, loading: true }
})
</script>
